import ClearIcon from '@mui/icons-material/Clear';
import { CircularProgress, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormLabel, Grid, IconButton, Radio, RadioGroup, Stack, TextField } from "@mui/material";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import { InterviewPreviewDialog } from 'Components/Interviews/InterviewPreviewDialog';
import { InterviewDialogStyled } from "Components/Interviews/Interviews.styled";
import { VideoInterviewTypes } from "Components/Interviews/InterviewsConstants";
import { ICreateInterviewPayload, ICreateZoomMeetPayload, InterviewType, VideoInterviewType } from 'Components/Interviews/InterviewsModel';
import { ZoomMeet } from 'Components/Interviews/ZoomMeet';
import { useNotification } from 'Components/Notification';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { useCallback, useEffect, useState } from 'react';
import { ApiState } from 'shared/SharedModels';
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { ShButton, ShGreenBtn } from 'shared/SharedStyles/ShInputs';
import { resetZoomCreation, resetZoomDelete, resetZoomUpdate } from 'store/slices/employer/applicants/applicant-actions-slice';
import { createInterviewSchedule, resetCreateInterview, resetUpdateInterviewSchedule, updateInterviewSchedule, updateInterviewStatus } from 'store/slices/employer/interviews/interviews-actions-slice';
import { getInterviewDetails, resetGetInterviewDetails } from 'store/slices/employer/interviews/interviews-details-slice';
import { closeInterviewDialog } from 'store/slices/employer/interviews/interviews-shared-slice';

export const InterviewDialog = () => {

    const dispatch = useAppDispatch();
    const notification = useNotification();
    const { zoomMeetCreationStatus, zoomMeetCreationResponse, zoomMeetResponse, zoomMeetUpdateResponse,
        zoomMeetUpdateStatus, zoomMeetDeleteStatus, zoomMeetDeleteResponse
    } = useAppSelector((state) => state.employer.applicantActions);
    const { createInterviewScheduleStatus, createInterviewScheduleResponse, updateInterviewScheduleStatus,
        updateInterviewScheduleResponse
    } = useAppSelector((state) => state.employer.interviewsActions);
    const { interviewDetails, getInterviewDetailsStatus } = useAppSelector((state) => state.employer.interviewsDetails);
    const { applicantInfo, candidateName, interviewId, isInterviewDialogOpen,
        jobId } = useAppSelector((state) => state.employer.interviewsShared);
    const [_interviewDetails, _setInterviewDetails] = useState<ICreateInterviewPayload>({
        application_id: undefined,
        from_time: '',
        info: '',
        note: '',
        schedule_date: '',
        to_time: '',
        type_of_interview: ''
    });

    const [zoomMeet, setZoomMeet] = useState<ICreateZoomMeetPayload>({
        agenda: '',
        from_time: '',
        password: '',
        schedule_date: '',
        topic: ''
    });
    const [videoInterviewType, setVideoInterviewType] = useState<VideoInterviewType | ''>('');
    const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState<boolean>(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);

    // Retrieve interview details callback.
    const fetchInterviewDetails = useCallback(() => {
        if (interviewId) {
            dispatch(getInterviewDetails({ id: interviewId }));
        }
    }, [dispatch, interviewId]);

    const _closeInterviewDialog = useCallback(() => {
        /*
        * Reset and clear every state and variable before closing dialog,
        *   to avoid any unexpected behavior. 
        * Since the model instance is not complete removed.
        */
        setZoomMeet({
            agenda: '',
            from_time: '',
            password: '',
            schedule_date: '',
            topic: ''
        });
        _setInterviewDetails({
            application_id: undefined,
            from_time: '',
            info: '',
            note: '',
            schedule_date: '',
            to_time: '',
            type_of_interview: ''
        });
        setVideoInterviewType('');
        setIsPreviewDialogOpen(false);
        setIsSubmitDisabled(false);
        dispatch(closeInterviewDialog());
        dispatch(resetCreateInterview());
        dispatch(resetGetInterviewDetails());
        dispatch(resetUpdateInterviewSchedule());
        dispatch(resetZoomCreation());
        dispatch(resetZoomUpdate());
        dispatch(resetZoomDelete());
    }, [dispatch]);

    // Load interview details if interview id is given.
    useEffect(() => {
        if (isInterviewDialogOpen) {
            fetchInterviewDetails();
        }
    }, [dispatch, fetchInterviewDetails, isInterviewDialogOpen]);

    // Load interview details if interview is edited.
    useEffect(() => {
        if (zoomMeetUpdateStatus === 'success' || zoomMeetDeleteStatus === 'success') {
            fetchInterviewDetails();
        }
    }, [fetchInterviewDetails, zoomMeetDeleteStatus, zoomMeetUpdateStatus]);

    // Populate interview details on edit mode.
    useEffect(() => {
        if (getInterviewDetailsStatus === 'success' && interviewDetails) {

            const { from_time, info, note, schedule_date, to_time, type_of_interview,
                zoom_meeting_id, zoom_meeting } = interviewDetails;
            _setInterviewDetails({
                from_time: from_time,
                info: info,
                note: note,
                schedule_date: schedule_date,
                to_time: to_time,
                type_of_interview: type_of_interview,
                zoom_meeting_id: zoom_meeting_id
            });

            if (type_of_interview === 'Video') {
                if (zoom_meeting_id && zoom_meeting?.response_data) {
                    setVideoInterviewType('zoom');
                    const { agenda, start_time, password, topic } = zoom_meeting?.response_data;

                    // retrieve date and time from 'start_time' since its in ISO format.
                    const dateTime = new Date(start_time);
                    const date = dateTime.toDateString();
                    const time = dateTime.toLocaleTimeString();

                    setZoomMeet({
                        agenda: agenda,
                        from_time: time,
                        password: password,
                        schedule_date: date,
                        topic: topic,
                        zoom_meeting_id: zoom_meeting_id
                    });
                } else {
                    setVideoInterviewType('other');
                }
            }
        }
    }, [dispatch, getInterviewDetailsStatus, interviewDetails, interviewId]);

    // Update zoom info into _interviewDetails once its created.
    useEffect(() => {
        if (zoomMeetCreationStatus === 'success') {
            _setInterviewDetails((prev) => {
                let newDetails = { ...prev };
                newDetails.zoom_meeting_id = zoomMeetResponse?.id;
                newDetails.info = zoomMeetResponse?.response?.agenda ?? prev.info;
                return newDetails;
            });
            setZoomMeet((prev) => {
                let newZoom = { ...prev };
                newZoom.agenda = zoomMeetResponse?.response?.agenda ?? prev.agenda;
                newZoom.zoom_meeting_id = zoomMeetResponse?.id;
                return newZoom;
            });
        }
    }, [zoomMeetCreationStatus, zoomMeetResponse?.id, zoomMeetResponse?.response?.agenda])

    const onInterviewModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const interviewType = e.target.value as InterviewType;
        // Reset 'zoomMeet' payload and 'videoInterviewType' if interview type is not video.
        if (interviewType !== 'Video') {
            setZoomMeet({
                agenda: '',
                from_time: '',
                password: '',
                schedule_date: '',
                topic: ''
            });
            setVideoInterviewType('');
        }
        _setInterviewDetails({ ..._interviewDetails, type_of_interview: interviewType });
    };

    // Disable submit button.
    useEffect(() => {
        const { from_time, info, note, schedule_date, to_time, type_of_interview } = _interviewDetails;
        setIsSubmitDisabled(!from_time || (videoInterviewType !== 'zoom' && !info) ||
            (videoInterviewType === 'zoom' && (!zoomMeet.agenda || !zoomMeet.password || !zoomMeet.zoom_meeting_id)) ||
            !note || !schedule_date || !to_time || !type_of_interview ||
            (_interviewDetails.from_time >= _interviewDetails.to_time));
    }, [_interviewDetails, videoInterviewType, zoomMeet.agenda, zoomMeet.password, zoomMeet.zoom_meeting_id]);

    const scheduleInterview = () => {
        setZoomMeet({
            ...zoomMeet,
            zoom_meeting_id: zoomMeetResponse?.id,
            from_time: _interviewDetails.from_time,
            schedule_date: _interviewDetails.schedule_date,
            topic: _interviewDetails.note
        });
        // Update interview payload with zoom details.
        const interviewPayload = { ..._interviewDetails };
        interviewPayload.zoom_meeting_id = zoomMeetResponse?.id;
        interviewPayload.info = zoomMeet.agenda;
        interviewPayload.application_id = applicantInfo?.candidateEmployerJobId;
        dispatch(createInterviewSchedule(interviewPayload));
    };

    const updateSchedule = () => {
        if (interviewDetails) {
            const { from_time, to_time, schedule_date, type_of_interview, info, note, zoom_meeting_id } = { ..._interviewDetails };
            dispatch(updateInterviewSchedule({
                id: interviewDetails.id,
                body: { from_time, to_time, schedule_date, type_of_interview, info, note, zoom_meeting_id }
            }));
        }
    };

    // Show snackbar notifications when interview schedule is successfully created.
    useEffect(() => {
        if (createInterviewScheduleStatus === 'success') {
            notification.displayNotification({
                open: true,
                type: 'success',
                message: createInterviewScheduleResponse
            });
            _closeInterviewDialog();
        }
    }, [_closeInterviewDialog, createInterviewScheduleResponse, createInterviewScheduleStatus, dispatch, notification]);

    // Show snackbar notifications when interview schedule is successfully updated.
    useEffect(() => {
        if (updateInterviewScheduleStatus === 'success') {
            notification.displayNotification({
                open: true,
                type: 'success',
                message: updateInterviewScheduleResponse
            });
            if (interviewId) {
                dispatch(updateInterviewStatus({ id: interviewId, body: { status: 'Scheduled', comments: 'Re scheduled' } }));
            }
            _closeInterviewDialog();
        }
    }, [_closeInterviewDialog, dispatch, interviewId, notification, updateInterviewScheduleResponse, updateInterviewScheduleStatus]);

    const showAlerts = (apiState: ApiState, pendingText: string, responseText: string, callBack?:
        ActionCreatorWithoutPayload<"applicantActions/resetZoomCreation"> |
        ActionCreatorWithoutPayload<"applicantActions/resetZoomUpdate"> |
        ActionCreatorWithoutPayload<"applicantActions/resetZoomDelete"> | undefined) => {
        switch (apiState) {
            case 'pending':
                return (<ShAlert marginBottom={2} severity="info">{pendingText}</ShAlert>);
            case 'success':
                return (<ShAlert marginBottom={2} severity="success"
                    onClose={() => callBack ? dispatch(callBack()) : ''}>{responseText}</ShAlert>);
            case 'failed':
                return (<ShAlert marginBottom={2} severity="error">{responseText}</ShAlert>);
            default:
                return (<></>);
        }
    };

    return (<>
        <InterviewDialogStyled open={isInterviewDialogOpen} onClick={e => e.stopPropagation()}
            onClose={() => _closeInterviewDialog()} aria-labelledby="title">
            <DialogTitle id='title' textAlign='center' fontWeight={600}>
                {getInterviewDetailsStatus === 'pending' ? 'Loading...' :
                    interviewDetails ? `${interviewDetails.status} - ${candidateName}` : 'Schedule Interview'}
                <IconButton className='close-btn' onClick={() => { _closeInterviewDialog() }}><ClearIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {showAlerts(zoomMeetCreationStatus, 'Creating Zoom Meet...', zoomMeetCreationResponse, resetZoomCreation)}
                {showAlerts(zoomMeetUpdateStatus, 'Updating Zoom Meet...', zoomMeetUpdateResponse, resetZoomUpdate)}
                {showAlerts(zoomMeetDeleteStatus, 'Deleting Zoom Meet...', zoomMeetDeleteResponse, resetZoomDelete)}
                {showAlerts(createInterviewScheduleStatus, 'Creating Interview schedule...', createInterviewScheduleResponse)}
                {showAlerts(updateInterviewScheduleStatus, 'Updating Interview schedule...', updateInterviewScheduleResponse)}
                {getInterviewDetailsStatus === 'pending' ?
                    <Stack justifyContent='center' alignItems='center'>
                        <CircularProgress />
                    </Stack> : <>
                        <Grid container spacing={2} justifyItems='self-start' marginBottom={2}>
                            <Grid item xs={12} sm={6} md={3} lg={3} xl={3} height='fit-content'>
                                <TextField id="from_time" label="From Time" variant="outlined" type='time' size='small' required
                                    fullWidth InputLabelProps={{ shrink: true }} value={_interviewDetails.from_time}
                                    onChange={e => { _setInterviewDetails({ ..._interviewDetails, from_time: e.target.value }) }} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} lg={3} xl={3} height='fit-content'>
                                <TextField id="to_time" label="To Time" variant="outlined" type='time' size='small' required
                                    error={_interviewDetails.to_time?.length > 0 && (_interviewDetails.from_time >= _interviewDetails.to_time)}
                                    helperText={_interviewDetails.to_time?.length > 0 && (_interviewDetails.from_time >= _interviewDetails.to_time) ?
                                        'To Time must be greater than From time' : ''}
                                    fullWidth InputLabelProps={{ shrink: true }} value={_interviewDetails.to_time}
                                    onChange={e => { _setInterviewDetails({ ..._interviewDetails, to_time: e.target.value }) }} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} lg={3} xl={3} height='fit-content'>
                                <TextField id="schedule_date" label="Date" variant="outlined" type='date' size='small' required
                                    fullWidth InputLabelProps={{ shrink: true }} value={_interviewDetails.schedule_date}
                                    // Only current and future dates are allowed to be selected
                                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                    onChange={e => { _setInterviewDetails({ ..._interviewDetails, schedule_date: e.target.value }) }} />
                            </Grid>
                        </Grid>
                        <Stack rowGap={2}>
                            <Stack direction='row'>
                                <FormControl fullWidth>
                                    <FormLabel id="mode-of-interview-label">Mode of interview*</FormLabel>
                                    <RadioGroup row aria-labelledby="mode-of-interview-label" id='mode_of_i'
                                        value={_interviewDetails.type_of_interview} onChange={onInterviewModeChange}>
                                        <FormControlLabel value="In-person" control={<Radio size="small" />} label="In-person" />
                                        <FormControlLabel value="Phone" control={<Radio size="small" />} label="Phone" />
                                        <FormControlLabel value="Video" control={<Radio size="small" />} label="Video" />
                                    </RadioGroup>
                                </FormControl>
                                {_interviewDetails.type_of_interview === 'Video' &&
                                    <FormControl fullWidth size='small'>
                                        <InputLabel id="duration-select-label">Interview Type</InputLabel>
                                        <Select label="Interview Type" value={videoInterviewType} id='i_t'
                                            onChange={e => setVideoInterviewType(e.target.value as VideoInterviewType)}>
                                            {VideoInterviewTypes.map(vIT => (
                                                <MenuItem key={vIT.value} value={vIT.value}>{vIT.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>}
                            </Stack>
                            {(applicantInfo || interviewDetails) && jobId && <>
                                {videoInterviewType === 'zoom' ? <ZoomMeet applicantInfo={applicantInfo} interviewDetails={_interviewDetails}
                                    setZoomMeet={setZoomMeet} zoomMeet={zoomMeet} jobId={jobId} interviewInfo={interviewDetails} /> :
                                    <TextField id="i_location" label={_interviewDetails.type_of_interview === 'In-person' ? "Interview Location/Address" :
                                        _interviewDetails.type_of_interview === 'Video' ? 'Video Link' : 'Phone number of the applicant'}
                                        variant="outlined" size='small' value={_interviewDetails.info}
                                        onChange={e => _setInterviewDetails({ ..._interviewDetails, info: e.target.value })} />
                                }
                            </>}
                            <TextField id="i_notes" label="Interview Notes" variant="outlined" multiline
                                size='small' value={_interviewDetails.note}
                                onChange={e => _setInterviewDetails({ ..._interviewDetails, note: e.target.value })} />
                        </Stack>
                    </>}

            </DialogContent>
            <DialogActions>
                {interviewDetails ?
                    <ShGreenBtn variant="contained" disableElevation size="small" onClick={updateSchedule}
                        className='self-right'>Submit</ShGreenBtn> :
                    <>
                        <Stack direction='row' justifyContent='space-between' width='100%'>
                            <ShButton variant="contained" disableElevation size="small"
                                onClick={() => setIsPreviewDialogOpen(true)}>Preview</ShButton>
                            <ShGreenBtn variant="contained" disableElevation size="small"
                                disabled={isSubmitDisabled || createInterviewScheduleStatus === 'pending'}
                                onClick={scheduleInterview}>Schedule and Email</ShGreenBtn>
                        </Stack>
                    </>
                }
            </DialogActions>
        </InterviewDialogStyled>
        {/* Preview dialog */}
        {applicantInfo && jobId &&
            <InterviewPreviewDialog isDialogOpen={isPreviewDialogOpen} setIsDialogOpen={setIsPreviewDialogOpen}
                applicantInfo={applicantInfo} interviewInfo={_interviewDetails} jobId={jobId} />
        }
    </>);
};